// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect } from "react-redux";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;

    return {
        totalItemCount: pagination?.totalItemCount,
        query: correctedQuery || productFilters.query,
        // xng-change: GUEST-2929 - add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
        currentBillTo: getCurrentBillToState(state).value,
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface ProductListProductCountStyles {
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
    // XNG-CHANGES
    categoryName?: TypographyPresentationProps;
    titleWrapper?: InjectableCss;
}

export const productCountStyles: ProductListProductCountStyles = {};

const styles = productCountStyles;

const ProductListProductCount: FC<Props> = ({ totalItemCount, query, xGoogleAnalytics, currentBillTo }) => {
    // xng-change: GUEST-2929 - add google event
    const getDataLayerEvent = () => {
        if (xGoogleAnalytics.includeSearchResult) {
            const dataLayer = getDataLayer();

            const googleAnalyticsData = {
                event: GOOGLE_EVENTS.SEARCH_RESULT,
                search_term: query,
                numSearchResults: totalItemCount,
                affilliation: currentBillTo?.companyName,
                brand_code: currentBillTo?.properties?.brand,
                exclusive: currentBillTo?.properties?.exclusive,
                group_descr: currentBillTo?.properties?.groupDescription,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };

    useEffect(() => {
        if (query?.length) {
            getDataLayerEvent();
        }
    }, [query]);

    const ItemCount = () => {
        if (!totalItemCount) {
            return null;
        }

        // XNg-Changes: change label
        const itemsText = totalItemCount !== 1 ? " Products" : " Product";

        return (
            <StyledWrapper {...styles.wrapper} className="productListCount-wrapper">
                <Typography {...styles.countText} data-test-selector="productListCountText" as="p">
                    {totalItemCount}&nbsp;
                </Typography>
                <Typography {...styles.itemsText} as="p">
                    {translate(itemsText)}
                </Typography>
                {/* {query && (
                <Typography {...styles.queryText} data-test-selector="productListQueryText" as="p">
                {query}
                </Typography>
            )} */}
            </StyledWrapper>
        );
    };

    // XNG-CHANGES
    if (query?.length) {
        return (
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.categoryName}>{`${translate("Products Result for")} "${query}"`} </Typography>
                <ItemCount />
            </StyledWrapper>
        );
    }

  

    return <ItemCount />;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListProductCount),
    definition: {
        group: "Product List",
        displayName: "Product List Count",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
